import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useAuth } from "../../accounts/AuthProvider";
import { ADMIN_BACKUP_FORMAT, adminRequest } from "../../accounts/adminApi";
import Card from "../UI/Card";
import Button from "../UI/Button";
import classes from "./AdminPage.module.css";

const DEFAULT_TEMPLATES = {
  invite: {
    subject: "Your Guitar Charts invitation",
    body: "Hello,\n\nYou have been invited to Guitar Charts. Use this link to accept your invitation and choose a password:\n\n{{action_url}}\n\nThis link is for {{email}}.\n\nGuitar Charts",
  },
  recovery: {
    subject: "Your Guitar Charts password reset",
    body: "Hello,\n\nUse this link to choose a new password for your Guitar Charts account:\n\n{{action_url}}\n\nThis link is for {{email}}.\n\nGuitar Charts",
  },
};
const TEMPLATE_STORAGE_KEY = "guitar-charts-admin-email-templates";
const date = value => value ? new Date(value).toLocaleString() : "Not recorded";
const safeFilename = value => (value || "account").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "account";

function fillTemplate(template, values) {
  return template.replace(/\{\{(email|action_url|site_url)\}\}/g, (_match, key) => values[key] ?? "");
}

function readBackup(value, targetId) {
  if (!value || value.format !== ADMIN_BACKUP_FORMAT || value.schemaVersion !== 1 ||
    value.account?.authUserId !== targetId || !Array.isArray(value.charts) ||
    (value.preferences !== null && (!value.preferences || typeof value.preferences !== "object" || Array.isArray(value.preferences)))) {
    throw new Error("The backup format or account identity is invalid.");
  }
  const ids = new Set();
  for (const chart of value.charts) {
    if (!chart || typeof chart !== "object" || typeof chart.id !== "string" ||
      !/^[\da-f]{8}(-[\da-f]{4}){3}-[\da-f]{12}$/i.test(chart.id) ||
      ids.has(chart.id) || typeof chart.libraryPath !== "string" ||
      !chart.document || typeof chart.document !== "object" || Array.isArray(chart.document)) {
      throw new Error("The backup contains an invalid or duplicate chart.");
    }
    ids.add(chart.id);
  }
  return value;
}

const AdminPage = () => {
  const { configured, loading: authLoading, session } = useAuth();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [pendingDisable, setPendingDisable] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [templateType, setTemplateType] = useState("invite");
  const [templateEmail, setTemplateEmail] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const accessToken = session?.access_token;

  const loadUsers = useCallback(async selectedPage => {
    if (!accessToken) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await adminRequest(accessToken, "list-users", { page: selectedPage });
      if (!Array.isArray(result.users) || typeof result.hasMore !== "boolean") {
        throw new Error("The account list response was invalid.");
      }
      setUsers(result.users);
      setHasMore(result.hasMore);
      setPage(selectedPage);
    } catch (failure) {
      setError(failure.message || "Could not load accounts.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) loadUsers(0);
  }, [accessToken, loadUsers]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(TEMPLATE_STORAGE_KEY);
      if (saved === null) return;
      const parsed = JSON.parse(saved);
      if (!parsed?.invite || !parsed?.recovery ||
        !["invite", "recovery"].every(type =>
          typeof parsed[type].subject === "string" && typeof parsed[type].body === "string")) {
        throw new Error("Saved email templates have an unsupported format.");
      }
      setTemplates(parsed);
    } catch (failure) {
      setError(failure.message || "Saved email templates could not be read.");
    }
  }, []);

  const saveTemplates = () => {
    try {
      window.localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(templates));
      setError("");
      setMessage("Email templates saved in this browser.");
    } catch {
      setError("Email templates could not be saved in this browser.");
    }
  };

  const currentTemplate = templates[templateType];
  const previewText = useMemo(() => emailPreview
    ? `${fillTemplate(currentTemplate.subject, emailPreview.values)}\n\n${fillTemplate(currentTemplate.body, emailPreview.values)}`
    : "", [currentTemplate, emailPreview]);

  const generateEmail = async event => {
    event.preventDefault();
    if (!accessToken || emailBusy) return;
    setEmailBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await adminRequest(accessToken, "create-email-link", {
        type: templateType,
        email: templateEmail.trim(),
      });
      setEmailPreview({
        values: { email: result.email, action_url: result.actionUrl, site_url: result.siteUrl },
      });
      setMessage(`One-time ${templateType === "invite" ? "invitation" : "password reset"} link created. Copy the email text and send it yourself.`);
    } catch (failure) {
      setError(failure.message || "The email link could not be generated.");
    } finally {
      setEmailBusy(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(previewText);
      setMessage("Email text copied. Send it only to the intended recipient.");
      setError("");
    } catch {
      setError("Could not access the clipboard. Select and copy the email text manually.");
    }
  };

  const toggleEnabled = async user => {
    if (!accessToken || busyId) return;
    setBusyId(user.id);
    setError("");
    setMessage("");
    try {
      await adminRequest(accessToken, "set-enabled", { targetUserId: user.id, enabled: !user.enabled });
      setPendingDisable(null);
      await loadUsers(page);
      setMessage(user.enabled
        ? `${user.email} is disabled. Private data access was blocked immediately; existing browser sessions may remain signed in until their access tokens expire.`
        : `${user.email} is enabled.`);
    } catch (failure) {
      await loadUsers(page);
      setError(failure.message || "The account status could not be changed.");
    } finally {
      setBusyId("");
    }
  };

  const deleteUser = async user => {
    if (!accessToken || busyId || deleteConfirmation !== user.email) return;
    setBusyId(user.id);
    setError("");
    setMessage("");
    try {
      await adminRequest(accessToken, "delete-user", { targetUserId: user.id });
      setPendingDelete(null);
      setDeleteConfirmation("");
      await loadUsers(page);
      setMessage(`Deleted ${user.email} and its associated private account data.`);
    } catch (failure) {
      await loadUsers(page);
      setError(failure.message || "The account could not be deleted.");
    } finally {
      setBusyId("");
    }
  };

  const downloadBackup = async user => {
    if (!accessToken || busyId) return;
    setBusyId(user.id);
    setError("");
    setMessage("");
    try {
      const backup = {
        format: ADMIN_BACKUP_FORMAT,
        schemaVersion: 1,
        exportedAt: new Date().toISOString(),
        account: { authUserId: user.id, email: user.email },
        charts: [],
        preferences: null,
      };
      let afterId = null;
      do {
        const result = await adminRequest(accessToken, "backup-page", {
          targetUserId: user.id,
          afterId,
        });
        if (!Array.isArray(result.charts)) throw new Error("The backup response was invalid.");
        backup.charts.push(...result.charts);
        if (!afterId) {
          backup.account.email = result.account?.email ?? user.email;
          backup.preferences = result.preferences ?? null;
        }
        afterId = result.nextAfterId ?? null;
      } while (afterId);
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `guitar-charts-backup-${safeFilename(user.email)}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setMessage(`Downloaded ${backup.charts.length} saved chart${backup.charts.length === 1 ? "" : "s"} and preferences for ${user.email}.`);
    } catch (failure) {
      setError(failure.message || "The account backup could not be created.");
    } finally {
      setBusyId("");
    }
  };

  const restoreBackup = async (user, file) => {
    if (!accessToken || busyId || !file) return;
    setBusyId(user.id);
    setError("");
    setMessage("");
    let jobId;
    try {
      const backup = readBackup(JSON.parse(await file.text()), user.id);
      if (!window.confirm(`Replace all saved charts and preferences for ${user.email} with ${backup.charts.length} charts from this backup? This cannot be undone.`)) return;
      const started = await adminRequest(accessToken, "restore-begin", {
        targetUserId: user.id,
        backup: {
          format: backup.format,
          schemaVersion: backup.schemaVersion,
          account: backup.account,
          chartCount: backup.charts.length,
          preferences: backup.preferences,
        },
      });
      jobId = started.jobId;
      const batchSize = Number.isSafeInteger(started.batchSize) && started.batchSize > 0 ? started.batchSize : 10;
      for (let index = 0; index < backup.charts.length; index += batchSize) {
        await adminRequest(accessToken, "restore-batch", {
          jobId,
          charts: backup.charts.slice(index, index + batchSize),
        });
      }
      const result = await adminRequest(accessToken, "restore-commit", { jobId });
      jobId = null;
      setMessage(`Restored ${result.restoredCharts} charts and preferences for ${user.email}. The account identity and enabled status were not changed.`);
    } catch (failure) {
      let failureMessage = failure.code === "ADMIN_OUTCOME_UNKNOWN"
        ? "The restore outcome could not be confirmed. Check the target account data before retrying."
        : failure.message || "The backup could not be restored.";
      if (jobId) {
        try {
          const cancelled = await adminRequest(accessToken, "restore-cancel", { jobId });
          if (cancelled.cancelled === true) failureMessage = "The restore did not complete; staged changes were cancelled and the existing data was kept.";
          else if (failure.code === "ADMIN_OUTCOME_UNKNOWN") {
            failureMessage += " Refresh or download a new backup to confirm the final state before trying again.";
          }
        } catch {
          failureMessage += " Private restore staging may remain until another restore is started.";
        }
      }
      setError(failureMessage);
    } finally {
      setBusyId("");
    }
  };

  const heading = "Administration";
  return <Card className={classes.panel}>
    <HelmetProvider><Helmet><title>{heading} | Guitar Charts</title><meta name="robots" content="noindex, nofollow" /></Helmet></HelmetProvider>
    <h1>{heading}</h1>
    {!configured ? <p>Accounts are not available on this version of the site.</p>
      : authLoading ? <p role="status">Checking your account…</p>
        : !session ? <p><Link to="/account">Sign in</Link> to continue.</p>
          : <>
            <p>Owner-only account tools. Invitation and reset links are one-time secrets: email them only to their intended recipients.</p>
            {error && <p role="alert">{error}</p>}
            {message && <p role="status">{message}</p>}

            <section aria-labelledby="users-heading">
              <div className={classes.sectionHeader}>
                <h2 id="users-heading">Registered accounts</h2>
                <Button disabled={loading || Boolean(busyId)} onClick={() => loadUsers(page)}>Refresh</Button>
              </div>
              <p>Shows account status and most recent sign-in reported by Supabase; it is not a live online-presence list. Disabling blocks private-data access immediately and prevents future sign-in/token refresh.</p>
              {loading ? <p role="status">Loading accounts…</p> : users.length === 0
                ? <p>No accounts on this page.</p>
                : <div className={classes.tableWrap}><table>
                  <thead><tr><th>Email</th><th>Status</th><th>Created</th><th>Last sign-in</th><th>Actions</th></tr></thead>
                  <tbody>{users.map(user => <tr key={user.id}>
                    <td>{user.email || "(no email)"}</td>
                    <td>{user.isOwner ? "Owner" : user.enabled ? "Enabled" : "Disabled"}</td>
                    <td>{date(user.createdAt)}</td>
                    <td>{date(user.lastSignInAt)}</td>
                    <td className={classes.userActions}>
                      {!user.isOwner && <>
                        {pendingDisable?.id === user.id
                          ? <div className={classes.confirm}>
                            <p>Disable {user.email}? Private data access will stop immediately.</p>
                            <Button disabled={Boolean(busyId)} onClick={() => toggleEnabled(user)}>Confirm disable</Button>
                            <button type="button" onClick={() => setPendingDisable(null)}>Cancel</button>
                          </div>
                          : <Button disabled={Boolean(busyId) || !user.accountExists} onClick={() => user.enabled
                            ? setPendingDisable(user) : toggleEnabled(user)}>
                            {busyId === user.id ? "Please wait…" : user.enabled ? "Disable" : "Enable"}
                          </Button>}
                        <Button disabled={Boolean(busyId)} onClick={() => {
                          setPendingDelete(user); setDeleteConfirmation("");
                        }}>Delete</Button>
                        {pendingDelete?.id === user.id && <div className={classes.confirm}>
                          <label htmlFor={`delete-confirm-${user.id}`}>Type {user.email} to confirm deletion</label>
                          <input id={`delete-confirm-${user.id}`} value={deleteConfirmation}
                            onChange={event => setDeleteConfirmation(event.target.value)} />
                          <Button disabled={Boolean(busyId) || deleteConfirmation !== user.email}
                            onClick={() => deleteUser(user)}>Delete account and data</Button>
                          <button type="button" onClick={() => {
                            setPendingDelete(null); setDeleteConfirmation("");
                          }}>Cancel</button>
                        </div>}
                      </>}
                      <Button disabled={Boolean(busyId) || !user.accountExists}
                        onClick={() => downloadBackup(user)}>Download backup</Button>
                      <label className={classes.fileButton}>
                        Restore backup
                        <input type="file" accept=".json,application/json" disabled={Boolean(busyId) || !user.accountExists}
                          onChange={event => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) restoreBackup(user, file);
                          }} />
                      </label>
                    </td>
                  </tr>)}</tbody>
                </table></div>}
              <div className={classes.pagination}>
                <Button disabled={loading || page === 0} onClick={() => loadUsers(page - 1)}>Previous</Button>
                <span>Page {page + 1}</span>
                <Button disabled={loading || !hasMore} onClick={() => loadUsers(page + 1)}>Next</Button>
              </div>
            </section>

            <section aria-labelledby="email-heading">
              <h2 id="email-heading">Email text</h2>
              <p>Generate a one-time link, edit and save the templates in this browser, then copy the complete text into your existing email. No email is sent automatically.</p>
              <form className={classes.form} onSubmit={generateEmail}>
                <label htmlFor="template-type">Email type</label>
                <select id="template-type" value={templateType} disabled={emailBusy}
                  onChange={event => { setTemplateType(event.target.value); setEmailPreview(null); }}>
                  <option value="invite">Invitation</option>
                  <option value="recovery">Password reset</option>
                </select>
                <label htmlFor="template-email">Recipient email</label>
                <input id="template-email" type="email" required value={templateEmail}
                  onChange={event => {
                    setTemplateEmail(event.target.value);
                    setEmailPreview(null);
                  }} />
                <label htmlFor="template-subject">Subject template</label>
                <input id="template-subject" value={currentTemplate.subject}
                  onChange={event => setTemplates(value => ({
                    ...value, [templateType]: { ...value[templateType], subject: event.target.value },
                  }))} />
                <label htmlFor="template-body">Message template</label>
                <textarea id="template-body" rows={9} value={currentTemplate.body}
                  onChange={event => setTemplates(value => ({
                    ...value, [templateType]: { ...value[templateType], body: event.target.value },
                  }))} />
                <p>Placeholders: <code>{"{{email}}"}</code>, <code>{"{{action_url}}"}</code>, <code>{"{{site_url}}"}</code>.</p>
                <div className={classes.controls}>
                  <Button type="button" onClick={saveTemplates}>Save email templates in this browser</Button>
                  <Button type="submit" disabled={emailBusy}>{emailBusy ? "Creating link…" : "Create email text"}</Button>
                </div>
              </form>
              {emailPreview && <div className={classes.preview}>
                <label htmlFor="email-preview">Copy-ready email</label>
                <textarea id="email-preview" rows={12} readOnly value={previewText} />
                <Button onClick={copyEmail}>Copy email text</Button>
              </div>}
            </section>
          </>}
    <p><Link to="/account">Return to your account</Link></p>
  </Card>;
};

export default AdminPage;
